'use client';

// module imports
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';

// file imports
import { SortableQuestionItem } from '@/components';
import { IQuestion } from '@/types';

interface QuestionSortableListProps {
  questions: IQuestion[];
  selectedQuestion: IQuestion | null;
  canEditQuestions: boolean;
  onReorder: (newOrder: IQuestion[]) => void;
  onQuestionSelect: (question: IQuestion) => void;
  onToggleQuestion: (questionId: string) => void;
}

export default function QuestionSortableList({ questions, selectedQuestion, canEditQuestions, onReorder, onQuestionSelect, onToggleQuestion }: QuestionSortableListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = questions.findIndex((item) => item._id === active.id);
      const newIndex = questions.findIndex((item) => item._id === over?.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(questions, oldIndex, newIndex);
        onReorder(newOrder);
      }
    }
  }

  // Filter questions that can be moved
  const movableQuestions = questions.filter((q) => q.isMovable);
  const movableQuestionIds = movableQuestions.map((q) => q._id);

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={movableQuestionIds} strategy={verticalListSortingStrategy}>
        {questions.map((question, index) => (
          <SortableQuestionItem key={question._id} question={question} isSelected={selectedQuestion?._id === question._id} isEnabled={question.isEnabled} onSelect={() => onQuestionSelect(question)} onToggle={() => onToggleQuestion(question._id)} canEditQuestions={canEditQuestions} index={index} />
        ))}
      </SortableContext>
    </DndContext>
  );
}
